import { Table, Button, UncontrolledTooltip, Input, Spinner } from "reactstrap";
import { withNamespaces } from "react-i18next";
import { EMPTY_LIST } from "src/common/constants";
import { Accordion } from "react-bootstrap";
import ShareholderImage from "src/modules/3rd-party-management/assets/images/shareholders.svg";
import { useHistory } from "react-router-dom";
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import KYBService from "src/modules/3rd-party-management/apis/admin/KYBService";
import { extractIndividualShareholders } from "src/modules/3rd-party-management/utils/shareholderGraph";
import ScreeningConfirmationModal from "../../../../components/screening-confirmation-modal";
import MonitoringConfirmationModal from "../../../../components/monitoring-confirmation-modal";
import StopMonitoringConfirmationModal from "../../../../components/stop-monitoring-confirmation-modal";

const Shareholders = ({
    t,
    activeAccordion,
    setActiveAccordion,
    detailsQuery,
    supplierData,
}) => {
    const history = useHistory();
    const idInSource = detailsQuery.data?.company?.idInSource;

    const [team, setTeam] = useState([]);
    const [selectedPersonToScreen, setSelectedPersonToScreen] = useState(null);
    const [selectedPersonToMonitor, setSelectedPersonToMonitor] = useState(null);
    const [selectedPersonToStopMonitoring, setSelectedPersonToStopMonitoring] =
        useState(null);
    const [showBulkSreenConfirmationModal, setShowBulkScreenConfirmationModal] =
        useState(false);
    const [selectedRowsForBulkAction, setSelectedRowsForBulkAction] = useState(
        []
    );

    const { isFetching: fetchingHolders } = useQuery({
        queryKey: [
            "3rd-party-management-kyb-get-organization-details",
            "ultimate-shareholder",
            idInSource,
        ],
        queryFn: async () => {
            const service = KYBService.getInstance();
            return await service.getUltimateShareholders(idInSource, {});
        },
        cacheTime: 10,
        refetchOnWindowFocus: false,
        enabled: !!idInSource,
        retry: 0,
        onSuccess: (data) => {
            setTeam(extractIndividualShareholders(data));
            setSelectedRowsForBulkAction([]);
        },
    });

    const onBulkCheckboxSelectAll = useCallback(
        (e) => {
            if (e.target.checked) {
                setSelectedRowsForBulkAction(team.map((item) => item.id));
            } else {
                setSelectedRowsForBulkAction([]);
            }
        },
        [team]
    );

    const handlePersonCheckboxCheck = useCallback((e) => {
        const { value, checked } = e.target;

        setSelectedRowsForBulkAction((prev) => {
            if (checked) {
                return [...prev, value];
            }

            return prev.filter((item) => item !== value);
        });
    }, []);

    const handleScreenCompleted = useCallback(() => {
        setSelectedPersonToScreen(null);
        setShowBulkScreenConfirmationModal(false);
        setSelectedRowsForBulkAction([]);
    }, []);

    const handleMonitorCompleted = useCallback(() => {
        setTeam((prev) =>
            prev.map((person) => {
                if (person.id === selectedPersonToMonitor?.id) {
                    return {
                        ...person,
                        underMonitoring: true,
                    };
                }

                return person;
            })
        );

        setSelectedPersonToMonitor(null);
    }, [selectedPersonToMonitor]);

    const handleStopMonitoringCompleted = useCallback(() => {
        setTeam((prev) =>
            prev.map((person) => {
                if (person.id === selectedPersonToStopMonitoring?.id) {
                    return {
                        ...person,
                        underMonitoring: false,
                    };
                }

                return person;
            })
        );

        setSelectedPersonToStopMonitoring(null);
    }, [selectedPersonToStopMonitoring]);

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion("individual-shareholders")}
            className={
                activeAccordion === "individual-shareholders" ? "active" : ""
            }
            eventKey="individual-shareholders"
        >
            <Accordion.Header>
                <img src={ShareholderImage} alt="" />
                <span>{t("Individual Shareholders")}</span>
            </Accordion.Header>

            <Accordion.Body>
                <div className="d-flex justify-content-end mb-3">
                    <span id="bulk-action-start-compliance-screening-btn">
                        <Button
                            color="primary"
                            outline
                            disabled={!selectedRowsForBulkAction.length}
                            onClick={() => setShowBulkScreenConfirmationModal(true)}
                        >
                            {!selectedRowsForBulkAction.length ? (
                                <UncontrolledTooltip target="bulk-action-start-compliance-screening-btn">
                                    {t(
                                        "Select at least one person to start screening."
                                    )}
                                </UncontrolledTooltip>
                            ) : null}

                            {t("Add to Screening")}
                        </Button>
                    </span>
                </div>

                <div
                    className="shareholders-section mb-5"
                    role="region"
                    aria-labelledby="shareholders-heading"
                >
                    <Table striped size="sm" aria-label="Shareholders table">
                        <caption className="visually-hidden">
                            {t(
                                "List of shareholders related to this organization."
                            )}
                        </caption>
                        <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 2,
                            }}
                        >
                            <tr>
                                <th style={{ width: "25px" }}>
                                    <Input
                                        type="checkbox"
                                        onChange={onBulkCheckboxSelectAll}
                                        checked={
                                            team.length > 0 &&
                                            selectedRowsForBulkAction.length ===
                                                team.length
                                        }
                                    />
                                </th>
                                <th scope="col">{t("Name")}</th>
                                <th scope="col">{t("Residence Address")}</th>
                                <th scope="col">{t("Percentage")}</th>
                                <th scope="col">{t("UBO")}</th>
                                <th scope="col">{t("Findings")}</th>
                                <th style={{ width: "300px" }}>{t("Action")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fetchingHolders ? (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        <Spinner size="sm" className="me-2" />
                                        {t("Fetching")}...
                                    </td>
                                </tr>
                            ) : team.length > 0 ? (
                                team.map((s) => (
                                    <tr key={s.id}>
                                        <td>
                                            <Input
                                                type="checkbox"
                                                checked={selectedRowsForBulkAction.includes(
                                                    s.id
                                                )}
                                                onChange={handlePersonCheckboxCheck}
                                                value={s.id}
                                            />
                                        </td>
                                        <td>{s.fullName}</td>
                                        <td>{s.address || "—"}</td>
                                        <td>
                                            {s.percentage
                                                ? `${s.percentage}%`
                                                : t("N/A")}
                                        </td>
                                        <td>{t(s.isUBO ? "Yes" : "No")}</td>
                                        <td>
                                            {s.hasFindings ? (
                                                <Button
                                                    color="link"
                                                    className="p-0"
                                                    onClick={() => {
                                                        history.push({
                                                            search: "?tab=findings",
                                                        });
                                                    }}
                                                >
                                                    <i className="ri-links-line align-middle me-1" />
                                                    {t("View")}
                                                </Button>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    color="primary"
                                                    onClick={() =>
                                                        setSelectedPersonToScreen(s)
                                                    }
                                                >
                                                    {t("Screen")}
                                                </Button>

                                                {isFeatureEnabledForActiveModule(
                                                    "compliance_monitoring_activation"
                                                ) && (
                                                    <>
                                                        {s.underMonitoring ? (
                                                            <Button
                                                                color="warning"
                                                                onClick={() =>
                                                                    setSelectedPersonToStopMonitoring(
                                                                        s
                                                                    )
                                                                }
                                                            >
                                                                {t("Stop Monitoring")}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                color="success"
                                                                onClick={() =>
                                                                    setSelectedPersonToMonitor(
                                                                        s
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "Add to Monitoring"
                                                                )}
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        {t(EMPTY_LIST)}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                <ScreeningConfirmationModal
                    isOpen={
                        !!selectedPersonToScreen || showBulkSreenConfirmationModal
                    }
                    toggle={() => {
                        setSelectedPersonToScreen(null);
                        setShowBulkScreenConfirmationModal(false);
                    }}
                    isBulkAction={showBulkSreenConfirmationModal}
                    supplierId={supplierData?.id}
                    people={
                        showBulkSreenConfirmationModal
                            ? selectedRowsForBulkAction
                            : selectedPersonToScreen
                              ? [selectedPersonToScreen]
                              : []
                    }
                    onCompleted={handleScreenCompleted}
                />

                <MonitoringConfirmationModal
                    isOpen={!!selectedPersonToMonitor}
                    toggle={() => setSelectedPersonToMonitor(null)}
                    person={selectedPersonToMonitor}
                    supplierId={supplierData?.id}
                    onCompleted={handleMonitorCompleted}
                />

                <StopMonitoringConfirmationModal
                    isOpen={!!selectedPersonToStopMonitoring}
                    toggle={() => setSelectedPersonToStopMonitoring(null)}
                    person={selectedPersonToStopMonitoring}
                    onCompleted={handleStopMonitoringCompleted}
                />
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default withNamespaces()(Shareholders);
